import { Badge, Box, Button, Flex, Text, Tooltip } from "@chakra-ui/react";
import { formatPercent, statusMeta, ThreadTurn } from "../ai-weekly-template-builder.utils";

interface Props {
  turns: ThreadTurn[];
  selectedId: ThreadTurn["suggestionId"] | null;
  isDisabled: boolean;
  onSelect: (turn: ThreadTurn) => void;
}

/**
 * The versions of one suggestion: v1 as generated, then one chip per round of
 * feedback.
 *
 * Sits at the top of the result panel because it is navigation, and because the
 * whole point of keeping every turn is that the coordinator can go back to an
 * earlier one and apply *that*. A single-turn conversation renders nothing —
 * there is no version to choose between.
 *
 * The server sends every version of the conversation, not just the ancestors of
 * the one on screen, so opening v2 of three keeps v3 on offer.
 */
export default function AiWeeklyTemplateVersionStrip(props: Props) {
  if (props.turns.length < 2) {
    return null;
  }

  // Only needed to name a branch's origin: refining an older version makes the
  // chip order stop matching the lineage.
  const versionBySuggestionId = new Map(
    props.turns.map((turn) => [Number(turn.suggestionId), turn.turnIndex])
  );

  return (
    <Box flexShrink={0} overflowX="auto">
      <Flex alignItems="center" gap={2} pb={1}>
        <Text color="gray.600" fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
          Versions
        </Text>
        {props.turns.map((turn) => {
          const meta = statusMeta(turn.status);
          const isSelected = props.selectedId === turn.suggestionId;
          const parentVersion =
            turn.parentSuggestionId === null
              ? null
              : versionBySuggestionId.get(Number(turn.parentSuggestionId)) ?? null;
          // Say where a version came from only when it is not the obvious
          // answer, i.e. when it was refined from something other than the chip
          // to its left.
          const origin =
            parentVersion === null || parentVersion === turn.turnIndex - 1
              ? ""
              : ` (from v${parentVersion})`;
          return (
            <Tooltip
              key={Number(turn.suggestionId)}
              hasArrow
              // The feedback that produced this version is the only thing that
              // tells the versions apart at a glance, so it is the tooltip.
              label={
                turn.feedback === null
                  ? "The original suggestion"
                  : `Asked for: ${turn.feedback}${origin}`
              }
              placement="top"
            >
              <Button
                colorScheme={isSelected ? "blue" : "gray"}
                isDisabled={props.isDisabled}
                size="sm"
                variant={isSelected ? "solid" : "outline"}
                onClick={() => props.onSelect(turn)}
              >
                v{turn.turnIndex}
                <Text as="span" fontSize="xs" fontWeight="normal" pl={2}>
                  {formatPercent(turn.realisticUtilizationPercent)}
                </Text>
                {turn.appliedAt !== null && (
                  <Badge colorScheme="purple" ml={2} variant="subtle">
                    Applied
                  </Badge>
                )}
                {meta.alert === "error" && (
                  <Badge colorScheme="red" ml={2} variant="subtle">
                    !
                  </Badge>
                )}
              </Button>
            </Tooltip>
          );
        })}
      </Flex>
    </Box>
  );
}
